import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  GitCompareArrows, RefreshCw, Smartphone, StopCircle, Download,
  SlidersHorizontal, MapPin, Activity, ListChecks, Wrench,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Notice, Pill, Stats, Stat, Item, Empty, Loading, Pager, Progress } from '../components/gtpl/GtplKit';

const FIELD_PREF_KEY = 'gtplVsCrm.compareFields';
// What runs started before the picker existed always compared — used to label their columns.
const LEGACY_FIELDS = ['mobile', 'entity', 'stbModel', 'stbSerial'];

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

// CRM value vs GTPL value, side by side; a differing pair is shown red.
function Cell({ label, crmVal, gtplVal, mismatched }) {
  const bad = mismatched ? { color: 'var(--g-bad, var(--danger))', fontWeight: 600 } : undefined;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, minWidth: 150, maxWidth: '100%' }}>
      <span style={{ fontSize: '.7rem', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</span>
      <span style={bad}><span style={{ color: 'var(--text-muted)', fontSize: '.7rem' }}>CRM </span>{crmVal || '—'}</span>
      <span style={bad}><span style={{ color: 'var(--text-muted)', fontSize: '.7rem' }}>GTPL </span>{gtplVal || '—'}</span>
    </span>
  );
}

// Compares active CATV customers' CRM data against the live copy on gtplsaathi.com. Which
// fields a run compares is the operator's choice ("What to compare" — the catalog comes from
// the backend via getCrmCheckFields, and each run records what it actually compared in
// run.fields, which is what the results draw their columns from). Running a check drives a real per-customer GTPL lookup in the
// background (there's no bulk "list every subscriber" endpoint on GTPL's side — see
// gtplCrmCheckService's own comment), so this page polls for progress rather than blocking on
// one request. Two separate fix directions, matching which side is the actual source of truth
// for each field: mobile is fixed ON GTPL ("Update GTPL's Data" — runs through Modify.aspx +
// raises GTPL's real "Update Customer Mobile Number" eSupport ticket, the only real mechanism
// for changing it, batched into one ticket for the whole bulk run); entity/box model/box
// number are fixed IN THE CRM instead ("Fix in CRM" — GTPL is the real source of truth for
// hardware assignment and entity, so this just copies GTPL's value into the Customer record,
// a plain local DB write, logged to the Change Ledger like any other customer edit).
export default function GtplVsCrmPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canCheck = hasModuleRight('gtplSaathi', 'crmCheck');
  const canFix = hasModuleRight('gtplSaathi', 'modify');

  const [run, setRun] = useState(null);
  const [fieldCatalog, setFieldCatalog] = useState([]);
  // What the NEXT run will compare (the run itself carries what it actually compared, in
  // run.fields). Remembered per browser so the same operator doesn't re-tick the same boxes
  // every time — the backend still defaults to the original four when nothing is sent.
  const [selectedFields, setSelectedFields] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FIELD_PREF_KEY) || 'null');
      return Array.isArray(saved) && saved.length ? saved : null;
    } catch { return null; }
  });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [onlyMismatches, setOnlyMismatches] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [fixingId, setFixingId] = useState(null);
  const [fixingAll, setFixingAll] = useState(false);
  const [fixingProfileId, setFixingProfileId] = useState(null);
  const [fixingAllProfiles, setFixingAllProfiles] = useState(false);
  const [fixingCrmId, setFixingCrmId] = useState(null);
  const [fixingAllInCrm, setFixingAllInCrm] = useState(false);
  const pollRef = useRef(null);

  const loadRows = useCallback(async (runId) => {
    if (!runId) { setRows([]); return; }
    setLoadingRows(true);
    try {
      const { data } = await gtplSaathiAPI.getCrmCheckRows({ runId, onlyMismatches, page, limit: 50 });
      setRows(data.rows || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load rows');
    } finally {
      setLoadingRows(false);
    }
  }, [onlyMismatches, page]);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getCrmCheckLatest();
      setRun(data.data || null);
      return data.data || null;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load check status');
      return null;
    }
  }, []);

  useEffect(() => { loadLatest().then((r) => loadRows(r?._id)); }, []); // eslint-disable-line

  useEffect(() => {
    gtplSaathiAPI.getCrmCheckFields()
      .then(({ data }) => {
        const catalog = data.data || [];
        setFieldCatalog(catalog);
        // Only fall back to the catalog's own defaults when nothing was remembered, and drop
        // any remembered key the backend no longer offers.
        setSelectedFields((prev) => {
          const valid = (prev || []).filter((k) => catalog.some((f) => f.key === k));
          return valid.length ? valid : catalog.filter((f) => f.default).map((f) => f.key);
        });
      })
      .catch(() => setFieldCatalog([]));
  }, []);

  useEffect(() => {
    if (selectedFields) { try { localStorage.setItem(FIELD_PREF_KEY, JSON.stringify(selectedFields)); } catch { /* private mode */ } }
  }, [selectedFields]);

  const toggleField = (key) => setSelectedFields((prev) => (
    (prev || []).includes(key) ? prev.filter((k) => k !== key) : [...(prev || []), key]
  ));

  useEffect(() => { if (run?._id) loadRows(run._id); }, [onlyMismatches, page]); // eslint-disable-line

  // Poll while a check or a bulk mobile-fix sweep is actively running in the background.
  useEffect(() => {
    const active = run?.status === 'running' || run?.mobileFixRunning || run?.profileFixRunning;
    if (!active) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const latest = await loadLatest();
      loadRows(latest?._id);
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [run?.status, run?.mobileFixRunning, run?.profileFixRunning, loadLatest, loadRows]);

  const handleRunCheck = async () => {
    if (selectedFields && !selectedFields.length) { toast.error('Pick at least one field to compare'); return; }
    setStarting(true);
    try {
      const { data } = await gtplSaathiAPI.runCrmCheck(selectedFields || undefined);
      setRun(data.data);
      setPage(1);
      setRows([]);
      toast.success('GTPL vs CRM check started — this runs in the background');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start check');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      const { data } = await gtplSaathiAPI.stopCrmCheck();
      setRun(data.data);
      toast.success('Stop requested — finishing the customer currently in progress, then it\'ll halt');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to stop');
    } finally {
      setStopping(false);
    }
  };

  const handleFixMobile = async (row) => {
    setFixingId(row._id);
    try {
      const { data } = await gtplSaathiAPI.fixCrmCheckMobile(row._id);
      if (data.data?.status === 'done') toast.success(data.data.message || 'GTPL mobile number updated');
      else if (data.data?.status === 'queued') toast(data.data.message || 'Queued — will run once a fresh session is saved', { icon: '⏳' });
      else toast.error(data.data?.message || 'Update failed');
      loadRows(run?._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
      loadRows(run?._id);
    } finally {
      setFixingId(null);
    }
  };

  const handleFixAll = async () => {
    if (!run?._id) return;
    if (!window.confirm('This raises ONE real GTPL eSupport ticket ("Update Customer Mobile Number") covering every customer whose CRM mobile number doesn\'t match GTPL. Continue?')) return;
    setFixingAll(true);
    try {
      const { data } = await gtplSaathiAPI.fixAllCrmCheckMobiles(run._id);
      if (data.data?.started) toast.success(`Updating ${data.data.count} mobile number(s) on GTPL — this runs in the background`);
      else toast('Nothing to update — no unresolved mobile mismatches', { icon: 'ℹ️' });
      const latest = await loadLatest();
      loadRows(latest?._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setFixingAll(false);
    }
  };

  const handleFixProfile = async (row) => {
    setFixingProfileId(row._id);
    try {
      const { data } = await gtplSaathiAPI.fixCrmCheckProfile(row._id);
      if (data.data?.status === 'queued') toast(data.data.message || 'Queued — will run once a fresh session is saved', { icon: '⏳' });
      else toast.success(data.data?.message || 'GTPL updated from the CRM');
      loadRows(run?._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
      loadRows(run?._id);
    } finally {
      setFixingProfileId(null);
    }
  };

  const handleFixAllProfiles = async () => {
    if (!run?._id) return;
    if (!window.confirm('This writes the CRM\'s name and address onto GTPL for every customer whose name or address doesn\'t match. GTPL may refuse individual subscribers (KYC-verified ones) — those are reported per row. Continue?')) return;
    setFixingAllProfiles(true);
    try {
      const { data } = await gtplSaathiAPI.fixAllCrmCheckProfiles(run._id);
      if (data.data?.started) toast.success(`Updating ${data.data.count} customer(s) on GTPL — this runs in the background`);
      else toast('Nothing to update — no unresolved name/address mismatches', { icon: 'ℹ️' });
      const latest = await loadLatest();
      loadRows(latest?._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setFixingAllProfiles(false);
    }
  };

  const handleFixInCrm = async (row) => {
    setFixingCrmId(row._id);
    try {
      const { data } = await gtplSaathiAPI.fixCrmCheckInCrm(row._id);
      toast.success(data.data?.message || 'CRM updated from GTPL');
      loadRows(run?._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setFixingCrmId(null);
    }
  };

  const handleFixAllInCrm = async () => {
    if (!run?._id) return;
    if (!window.confirm('This copies GTPL\'s entity / box model / box number into the CRM record for every mismatched customer. Continue?')) return;
    setFixingAllInCrm(true);
    try {
      const { data } = await gtplSaathiAPI.fixAllCrmCheckInCrm(run._id);
      if (data.data?.total) toast.success(`Updated ${data.data.fixed} of ${data.data.total} customer(s) in the CRM${data.data.failed ? `, ${data.data.failed} failed` : ''}`);
      else toast('Nothing to update — no unresolved entity/box mismatches', { icon: 'ℹ️' });
      loadRows(run._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setFixingAllInCrm(false);
    }
  };

  // The columns to render: whatever the loaded run actually compared, in catalog order, with
  // the field's `store` key telling each Cell where to read its pair from on the row.
  const columns = (() => {
    const keys = run?.fields?.length ? run.fields : LEGACY_FIELDS;
    if (!fieldCatalog.length) {
      return keys.map((k) => ({ key: k, label: k, store: k === 'entity' ? 'entityCode' : k }));
    }
    return fieldCatalog.filter((f) => keys.includes(f.key));
  })();

  // Which fix directions make sense for the run being displayed — a run that didn't compare
  // mobile has no mobile mismatches to push to GTPL, and likewise for the CRM direction.
  const mobileCompared = columns.some((c) => c.key === 'mobile');
  const profileCompared = columns.some((c) => ['name', 'address'].includes(c.key));
  const crmFixCompared = columns.some((c) => ['entity', 'stbModel', 'stbSerial'].includes(c.key));

  const isRunning = run?.status === 'running';
  const mismatchCount = (run?.mismatches || 0) + (run?.errorCount || 0);
  const runTone = isRunning ? 'info' : run?.status === 'failed' ? 'bad' : run?.status === 'cancelled' ? 'mute' : 'ok';
  const runLabel = isRunning ? (run.cancelRequested ? 'Stopping…' : 'Running') : run?.status === 'failed' ? 'Failed' : run?.status === 'cancelled' ? 'Stopped' : 'Completed';
  const sel = selectedFields || [];
  const showFixBar = (mismatchCount > 0 || run?.mobileFixRunning || run?.profileFixRunning) && (mobileCompared || profileCompared || crmFixCompared);

  // Result line for a fix: done = green, queued = amber, failed = red.
  const fixState = (fix, doneText) => {
    if (fix?.status === 'done') return <Pill tone="ok" dot>{fix.message || doneText}</Pill>;
    if (fix?.status === 'queued') return <Pill tone="warn" dot>Queued</Pill>;
    return null;
  };

  return (
    <Screen>
      <PageHead icon={GitCompareArrows} tone="ok" title="GTPL vs CRM"
        sub="Compares every active CableTV customer's CRM record against the live subscriber record on gtplsaathi.com — tick the fields you want compared below. This walks each customer one at a time on GTPL's side, so a full run can take a while — it keeps running in the background even if you navigate away.">
        {(isRunning || run?.mobileFixRunning || run?.profileFixRunning) && (
          <button type="button" className="btn btn-sm btn-danger" disabled={stopping || run?.cancelRequested || !canCheck} onClick={handleStop}>
            <StopCircle size={14} /> {run?.cancelRequested || stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-primary" disabled={starting || isRunning || !canCheck} onClick={handleRunCheck}>
          <RefreshCw size={14} style={starting || isRunning ? { animation: 'spin 1s linear infinite' } : undefined} />
          {isRunning ? 'Checking…' : starting ? 'Starting…' : 'Run GTPL vs CRM Check'}
        </button>
      </PageHead>

      {!canCheck && <Notice tone="bad">You don't have permission to run the GTPL vs CRM check.</Notice>}

      {fieldCatalog.length > 0 && (
        <Panel icon={SlidersHorizontal} tone="info" title="What to compare" collapsible open={!run} count={`${sel.length} / ${fieldCatalog.length}`}
          right={<>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={isRunning}
              onClick={() => setSelectedFields(fieldCatalog.map((f) => f.key))}>Select all</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={isRunning}
              onClick={() => setSelectedFields(fieldCatalog.filter((f) => f.default).map((f) => f.key))}>Default</button>
          </>}
          sub={sel.length === 0
            ? 'Pick at least one field.'
            : fieldCatalog.some((f) => f.needsHardware && sel.includes(f.key))
              ? 'Box model / box number need a second lookup per customer — unticking both makes a full run roughly twice as fast.'
              : 'No box fields selected, so this run skips the STB lookup — roughly twice as fast.'}>
          <div className="gx-list">
            {fieldCatalog.map((f) => {
              const on = sel.includes(f.key);
              return (
                <label key={f.key} className={`gx-item${on ? ' is-selected' : ''}`}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: isRunning ? 'default' : 'pointer', padding: '9px 12px' }}>
                  <input type="checkbox" checked={on} disabled={isRunning} onChange={() => toggleField(f.key)} style={{ marginTop: 3 }} />
                  <span style={{ flex: 1 }}>
                    <b>{f.label}</b>
                    <div className="text-small" style={{ color: 'var(--text-muted)' }}>
                      {f.fix === 'gtpl' ? 'Fixable on GTPL' : f.fix === 'crm' ? 'Fixable in CRM' : 'Check only'}
                      {f.needsHardware ? ' · extra GTPL lookup' : ''}
                    </div>
                  </span>
                  {f.fix === 'gtpl' && <Pill tone="adv">GTPL fix</Pill>}
                  {f.fix === 'crm' && <Pill tone="info">CRM fix</Pill>}
                </label>
              );
            })}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '.78rem', marginTop: 10 }}>
            Only "Fixable" fields can be corrected from the results; the rest are reported for you to act on.
          </div>
        </Panel>
      )}

      <Panel icon={Activity} tone={run ? runTone : 'mute'} title={run ? `Latest check: ${runLabel}` : 'Latest check'}
        sub={run ? `${isRunning ? 'Started' : 'Completed'} ${fmtDateTime(isRunning ? run.startedAt : run.completedAt)}` : undefined}>
        {!run ? (
          <Empty icon={GitCompareArrows} title="No check has been run yet" />
        ) : (
          <>
            <Stats>
              <Stat tone="info" value={`${run.checked || 0} / ${run.totalCustomers || 0}`} label="Checked" />
              <Stat tone="ok" value={run.matches || 0} label="Matches" />
              <Stat tone={mismatchCount ? 'bad' : 'mute'} value={run.mismatches || 0} label="Mismatches" />
              <Stat tone={run.errorCount ? 'warn' : 'mute'} value={run.errorCount || 0} label="Errors" />
            </Stats>
            {isRunning && <Progress value={run.checked || 0} max={run.totalCustomers || 0} tone="info" />}
            {run.failMessage && (
              <div style={{ marginTop: 12 }}>
                <Notice tone={run.status === 'cancelled' ? 'mute' : 'bad'}
                  actions={/session expired/i.test(run.failMessage) ? <Link to="/gtpl-saathi-settings" style={{ fontWeight: 600 }}>Paste a fresh cookie →</Link> : null}>
                  {run.failMessage}
                </Notice>
              </div>
            )}
          </>
        )}
      </Panel>

      {run && showFixBar && (
        <Panel icon={Wrench} tone="adv" title="Fix everything at once"
          sub="Mobile and name/address are written to GTPL; entity and box are copied into the CRM.">
          <div className="gx-actions">
            {mobileCompared && (
              <button type="button" className="btn btn-sm btn-primary" disabled={fixingAll || run.mobileFixRunning || !canFix} onClick={handleFixAll}>
                <Smartphone size={13} />
                {run.mobileFixRunning
                  ? `Updating GTPL's Data… (${run.mobileFixDone || 0}/${run.mobileFixTotal || 0})`
                  : fixingAll ? 'Starting…' : "Update GTPL's Data (fix mismatched mobiles)"}
              </button>
            )}
            {profileCompared && (
              <button type="button" className="btn btn-sm btn-primary" disabled={fixingAllProfiles || run.profileFixRunning || !canFix} onClick={handleFixAllProfiles}>
                <MapPin size={13} />
                {run.profileFixRunning
                  ? `Updating GTPL's Data… (${run.profileFixDone || 0}/${run.profileFixTotal || 0})`
                  : fixingAllProfiles ? 'Starting…' : "Update GTPL's Data (fix mismatched names / addresses)"}
              </button>
            )}
            {crmFixCompared && (
              <button type="button" className="btn btn-sm btn-secondary" disabled={fixingAllInCrm || !canCheck} onClick={handleFixAllInCrm}>
                <Download size={13} /> {fixingAllInCrm ? 'Updating…' : 'Fix in CRM (entity / box model / box number)'}
              </button>
            )}
          </div>
          {mobileCompared && !canFix && <div className="text-small text-muted" style={{ marginTop: 8 }}>Requires Modify rights on GTPL Saathi.</div>}
        </Panel>
      )}

      {run && (
        <Panel icon={ListChecks} tone={mismatchCount ? 'bad' : 'mute'} title="Results" count={pagination.total}
          right={
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loadingRows} onClick={() => loadRows(run._id)}>
              <RefreshCw size={13} style={loadingRows ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
            </button>}>
          <div className="gx-actions" style={{ marginBottom: 12 }}>
            <button type="button" aria-pressed={onlyMismatches} className={`btn btn-sm ${onlyMismatches ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setOnlyMismatches(!onlyMismatches); setPage(1); }}>
              Only mismatches / errors
            </button>
          </div>

          {loadingRows ? (
            <Loading />
          ) : rows.length === 0 ? (
            <Empty tone={onlyMismatches ? 'ok' : undefined}
              title={onlyMismatches ? 'No mismatches or errors found' : (isRunning ? 'Results will appear here as the check progresses…' : 'Nothing to show')} />
          ) : (
            <div className="gx-list">
              {rows.map((r) => {
                const diff = new Set(r.diffFields || []);
                const fixing = fixingId === r._id;
                const fixingProfile = fixingProfileId === r._id;
                // One Modify.aspx save covers both, so the button says which it will push.
                const profileFixable = diff.has('name') || diff.has('address');
                const profileFixLabel = diff.has('name') && diff.has('address') ? 'Name + Address'
                  : diff.has('name') ? 'Name' : 'Address';
                const fixingCrm = fixingCrmId === r._id;
                const crmFixable = diff.has('entity') || diff.has('stbModel') || diff.has('stbSerial');
                const tone = r.status === 'error' ? 'warn' : r.status === 'mismatch' || diff.size ? 'bad' : r.status === 'match' ? 'ok' : undefined;
                return (
                  <Item key={r._id} tone={tone}
                    title={<Link to={`/customers/${r.customer}`}>{r.name}</Link>}
                    sub={<>Bill No. {r.billingNumber || '—'} · <span style={{ fontFamily: 'monospace' }}>{r.accountNumber}</span></>}
                    right={r.status === 'error' ? <Pill tone="warn" dot>Error</Pill> : diff.size ? <Pill tone="bad" dot>{diff.size} differ{diff.size === 1 ? 's' : ''}</Pill> : <Pill tone="ok" dot>Match</Pill>}
                    meta={<>
                      {r.status === 'error' && <Pill tone="bad">{r.error}</Pill>}
                      {columns.map((c) => (
                        <Cell key={c.key} label={c.label}
                          crmVal={r.crm?.[c.store]}
                          gtplVal={c.key === 'entity' ? (r.gtpl?.entityCode || r.gtpl?.entity) : r.gtpl?.[c.store]}
                          mismatched={diff.has(c.key)} />
                      ))}
                    </>}
                    actions={<>
                      {diff.has('mobile') && (fixState(r.mobileFix, 'Updated') || (
                        <button type="button" className="btn btn-sm btn-primary" disabled={fixing || !canFix} onClick={() => handleFixMobile(r)}>
                          <Smartphone size={12} style={fixing ? { animation: 'spin 1s linear infinite' } : undefined} /> {fixing ? 'Updating…' : "Update GTPL's Data"}
                        </button>
                      ))}
                      {r.mobileFix?.status === 'failed' && <Pill tone="bad">{r.mobileFix.message}</Pill>}
                      {profileFixable && (fixState(r.profileFix, 'Updated on GTPL') || (
                        <button type="button" className="btn btn-sm btn-primary" disabled={fixingProfile || !canFix} onClick={() => handleFixProfile(r)}>
                          <MapPin size={12} style={fixingProfile ? { animation: 'spin 1s linear infinite' } : undefined} /> {fixingProfile ? 'Updating…' : `Update GTPL's ${profileFixLabel}`}
                        </button>
                      ))}
                      {r.profileFix?.status === 'failed' && <Pill tone="bad">{r.profileFix.message}</Pill>}
                      {crmFixable && (r.crmFix?.status === 'done' ? <Pill tone="ok" dot>{r.crmFix.message || 'Updated'}</Pill> : (
                        <button type="button" className="btn btn-sm btn-secondary" disabled={fixingCrm || !canCheck} onClick={() => handleFixInCrm(r)}>
                          <Download size={12} style={fixingCrm ? { animation: 'spin 1s linear infinite' } : undefined} /> {fixingCrm ? 'Updating…' : 'Fix in CRM'}
                        </button>
                      ))}
                      {r.crmFix?.status === 'failed' && <Pill tone="bad">{r.crmFix.message}</Pill>}
                    </>} />
                );
              })}
            </div>
          )}

          <Pager page={page} pages={pagination.pages} total={pagination.total} onPage={setPage} />
        </Panel>
      )}
    </Screen>
  );
}
